/** Display helpers + token colors for statuses, priorities, severities. */

import type { IconName } from './icons';

export const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', triage: 'Triage', backlog: 'P · Backlog (New)',
  design_needed: 'P · Design Needed', acceptance_criteria_needed: 'P · Acceptance Criteria Needed',
  estimate_needed: 'P · Estimate Needed', needs_approval: 'P · Needs Approval', ready_for_qa: 'E · Ready for QA',
  approved: 'Approved', approved_for_agent: 'Approved · Agent', in_progress: 'In Progress',
  agent_in_progress: 'Agent Working', internal_waiting: 'Internal Waiting', dev_done: 'Dev Done', qa: 'QA In Progress', ready_for_uat: 'Ready for UAT', uat: 'UAT',
  staging: 'Staging', done: 'Done',
  cancelled: 'Cancelled', rejected: 'Rejected',
  qa_uat: 'QA / UAT', // combined swimlane stage
};

export const STATUS_COLOR: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-sky-100 text-sky-800',
  triage: 'bg-amber-100 text-amber-800',
  backlog: 'bg-violet-100 text-violet-800',
  design_needed: 'bg-violet-100 text-violet-800',
  acceptance_criteria_needed: 'bg-violet-100 text-violet-800',
  estimate_needed: 'bg-violet-100 text-violet-800',
  needs_approval: 'bg-violet-100 text-violet-800',
  ready_for_qa: 'bg-blue-100 text-blue-800',
  approved: 'bg-indigo-100 text-indigo-800',
  approved_for_agent: 'bg-violet-100 text-violet-800',
  in_progress: 'bg-blue-100 text-blue-800',
  agent_in_progress: 'bg-violet-100 text-violet-800',
  internal_waiting: 'bg-orange-100 text-orange-800',
  dev_done: 'bg-teal-100 text-teal-800',
  qa: 'bg-fuchsia-100 text-fuchsia-800',
  ready_for_uat: 'bg-cyan-100 text-cyan-800',
  uat: 'bg-purple-100 text-purple-800',
  staging: 'bg-cyan-100 text-cyan-800',
  done: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-200 text-slate-600',
  rejected: 'bg-rose-100 text-rose-800',
};

export const PRIORITY_COLOR: Record<string, string> = {
  lowest: 'text-slate-400', low: 'text-sky-500', medium: 'text-amber-500',
  high: 'text-orange-500', highest: 'text-rose-600',
};

export const PRIORITY_LABEL: Record<string, string> = {
  lowest: 'Lowest', low: 'Low', medium: 'Medium', high: 'High', highest: 'Highest',
};

export const SEVERITY_LABEL: Record<string, string> = {
  s1: 'S1 · Critical', s2: 'S2 · Major', s3: 'S3 · Minor', s4: 'S4 · Trivial',
};

export const TYPE_LABEL: Record<string, string> = {
  customer_support: 'Support', internal_bug: 'Bug', feature_request: 'Feature', change_request: 'CR (Change Request)',
  task: 'Task', tech_debt: 'TechDebt', epic: 'Epic', subtask: 'Subtask', infrastructure: 'DevOps',
};

export const TYPE_ICON: Record<string, IconName> = {
  customer_support: 'support', internal_bug: 'bug', feature_request: 'feature', change_request: 'sync',
  task: 'tasks', tech_debt: 'tasks', epic: 'epic', subtask: 'subtask', infrastructure: 'infrastructure',
};

/** Color-coded work-item-type pill classes (Azure-DevOps style). */
export const TYPE_BADGE: Record<string, string> = {
  epic: 'bg-amber-100 text-amber-800',
  feature_request: 'bg-violet-100 text-violet-800',
  change_request: 'bg-orange-100 text-orange-800',
  task: 'bg-sky-100 text-sky-800',
  tech_debt: 'bg-lime-100 text-lime-800',
  internal_bug: 'bg-rose-100 text-rose-800',
  customer_support: 'bg-teal-100 text-teal-800',
  infrastructure: 'bg-indigo-100 text-indigo-800',
  subtask: 'bg-slate-100 text-slate-700',
};

/** Left-accent / bar colors per work-item type (hex for inline styles). */
export const TYPE_ACCENT: Record<string, string> = {
  epic: '#f59e0b', feature_request: '#8b5cf6', change_request: '#c2410c', task: '#0ea5e9', tech_debt: '#65a30d',
  internal_bug: '#f43f5e', customer_support: '#14b8a6', infrastructure: '#6366f1', subtask: '#94a3b8',
};

/** Words that keep a fixed casing when they appear inside an enum value. */
const SPECIAL_WORD: Record<string, string> = {
  qa: 'QA In Progress', uat: 'UAT', ui: 'UI', api: 'API', ai: 'AI', arr: 'ARR', sso: 'SSO',
  id: 'ID', url: 'URL', csv: 'CSV', pr: 'PR', ok: 'OK',
  s1: 'S1', s2: 'S2', s3: 'S3', s4: 'S4',
  hubspot: 'HubSpot', github: 'GitHub', ira: 'IRA', po: 'PO',
};

/**
 * Universal display fallback for raw enum/snake_case values: splits on `_` and
 * whitespace, Title-Cases each word, and applies acronym/brand casing
 * (qa → QA, hubspot → HubSpot, …). Display only — never feed the result back
 * into the API; wire values stay snake_case.
 *
 * Prefer the authoritative maps (STATUS_LABEL, PRIORITY_LABEL, SEVERITY_LABEL,
 * TYPE_LABEL) when the value is one of those enums; use `enumLabel` for
 * everything else.
 */
export function enumLabel(value: string): string {
  if (!value) return value;
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => SPECIAL_WORD[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function timeAgo(iso: string): string {
  const d = new Date(iso).getTime();
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/** Date only, day–month-name–year (e.g. "8 July 2026"). Null/empty → em dash. */
export function formatDate(iso: string | null | undefined, opts?: { utc?: boolean }): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', ...(opts?.utc ? { timeZone: 'UTC' } : {}),
  });
}

/** Exact date + time, day–month-name–year (e.g. "8 July 2026, 3:42 PM"). */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}
